import * as React from 'react';
import manageAlarmsStyle from '../ManageAlarmsStyle';
import { Box, Checkbox, FormControlLabel, TextField } from '@mui/material';
import * as PXBColors from '@brightlayer-ui/colors';
import { useLanguageLocale } from '../../../hooks/language-locale-hooks';
import { AlarmConfigData, ManageAdvAlarmConfig } from '../../../types/manage-alarm-type';
import { DECIMAL_NUMBER } from '../../../constants';

export const ConfigureAdvAlarms: React.FC<{
    index: number;
    value: number;
    alarmConfigData: AlarmConfigData | null;
    updateAdvAlarmData: Function;
}> = (props) => {
    const { t } = useLanguageLocale();
    const classes = manageAlarmsStyle();

    const { value, index, ...other } = props;

    const [alarmConfig, setAlarmConfig] = React.useState<ManageAdvAlarmConfig | null>(null);

    const onChangeValues = (val: number | string | boolean, name: string, property?: string): void => {
        if (alarmConfig) {
            const alarmConfigObj: any = { ...alarmConfig };
            if (!property) alarmConfigObj[`${name}`] = val;
            else alarmConfigObj[`${name}`][`${property}`] = val;
            setAlarmConfig(alarmConfigObj);
            props.updateAdvAlarmData(alarmConfigObj);
        }
    };

    const onNumChange = (
        e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
        name: string,
        property?: string
    ): void => {
        const numValue = e.target.value;
        if (new RegExp(DECIMAL_NUMBER).test(numValue) || numValue === '') {
            onChangeValues(e.target.value, name, property);
        }
    };

    const onCheckChange = (val: boolean, name: string, property?: string): void => {
        onChangeValues(val, name, property);
    };

    React.useEffect(() => {
        if (props.alarmConfigData) {
            if (props.alarmConfigData.alarmConfigurations.length === 0) {
                const defaultValue: ManageAdvAlarmConfig = {
                    ackHoldOff: {
                        checked: false,
                        time: 0,
                    },
                    rearm: {
                        checked: false,
                        time: 0,
                    },
                    alarmDelay: {
                        checked: false,
                        time: 0,
                    },
                    latching: false
                };
                setAlarmConfig(defaultValue);
            } else setAlarmConfig(props.alarmConfigData?.alarmConfigurations[0].alarmConfiguration);
        }
    }, []);

    return (
        <>
            {alarmConfig && (
                <div
                    role="tabpanel"
                    hidden={value !== index}
                    id={`adv-tabpanel-${index}`}
                    aria-labelledby={`adv-tab-${index}`}
                    {...other}
                    style={{ maxHeight: '62vh', minHeight: '62vh', overflow: 'auto' }}
                >
                    <Box sx={(alarmConfig.ackHoldOff.checked)?{ background:PXBColors.blue[50]}: {color:'black' }} className={classes.marginpadding8}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={alarmConfig.ackHoldOff.checked}
                                    data-testid="ackHoldOff"
                                    value={alarmConfig.ackHoldOff.checked}
                                    onChange={(event): void =>
                                        onCheckChange(event.target.checked, 'ackHoldOff', 'checked')
                                    }
                                />
                            }
                            label={t('MANAGEALARMS.ACK_HOLD')}
                        />
                    </Box>
                    <Box className={classes.paddingTextbox}>
                        <TextField
                            inputProps={{ 'data-testid': 'limit' }}
                            fullWidth
                            variant="filled"
                            id="Time(min)"
                            label="Time(min)"
                            value={alarmConfig.ackHoldOff.time}
                            onChange={(e): void => {
                                onNumChange(e, 'ackHoldOff', 'time');
                            }}
                        />
                    </Box>
                    <Box sx={(alarmConfig.rearm.checked)?{ background:PXBColors.blue[50]}: {color:'black' }} className={classes.marginpadding8}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={alarmConfig.rearm.checked}
                                    data-testid="rearm"
                                    value={alarmConfig.rearm.checked}
                                    onChange={(event): void => onCheckChange(event.target.checked, 'rearm', 'checked')}
                                />
                            }
                            label={t('MANAGEALARMS.RE_ARM')}
                        />
                    </Box>
                    <Box className={classes.paddingTextbox}>
                        <TextField
                            inputProps={{ 'data-testid': 'message' }}
                            fullWidth
                            variant="filled"
                            id="Time(min)"
                            label="Time(min)"
                            value={alarmConfig.rearm.time}
                            onChange={(e): void => {
                                onNumChange(e, 'rearm', 'time');
                            }}
                        />
                    </Box>

                    <Box sx={(alarmConfig.alarmDelay.checked)?{ background:PXBColors.blue[50]}: {color:'black' }} className={classes.marginpadding8}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={alarmConfig.alarmDelay.checked}
                                    data-testid="alarmDelay"
                                    value={alarmConfig.alarmDelay.checked}
                                    onChange={(event): void =>
                                        onCheckChange(event.target.checked, 'alarmDelay', 'checked')
                                    }
                                />
                            }
                            label={t('MANAGEALARMS.ALARM_DELAY')}
                        />
                    </Box>
                    <Box className={classes.paddingTextbox}>
                        <TextField
                            inputProps={{ 'data-testid': 'message' }}
                            fullWidth
                            variant="filled"
                            id="Time(min)"
                            label="Time(min)"
                            value={alarmConfig.alarmDelay.time}
                            onChange={(e): void => {
                                onNumChange(e, 'alarmDelay', 'time');
                            }}
                        />
                    </Box>

                    <Box sx={(alarmConfig.latching)?{ background:PXBColors.blue[50]}: {color:'black' }} className={classes.marginpadding8}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={alarmConfig.latching}
                                    data-testid="latching"
                                    value={alarmConfig.latching}
                                    onChange={(event): void => onCheckChange(event.target.checked, 'latching')}
                                />
                            }
                            label={t('MANAGEALARMS.LATCH_ALARM')}
                        />
                    </Box>
                </div>
            )}
        </>
    );
};
